import type { components } from "@brandex/api-client";
import { useBrandex } from "@brandex/dashboard";
import { useQuery } from "@tanstack/react-query";

export type StaffPermission = components["schemas"]["StaffPermissionDto"];
export type StaffAccess = components["schemas"]["StaffAccessDto"];

export const accessKey = ["me", "staff-access"] as const;

/** The signed-in staff member's permissions (ADR 0022), for showing only what they can use. */
export function useStaffAccess() {
  const { api } = useBrandex();
  return useQuery<StaffAccess>({
    queryKey: accessKey,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/me/staff-access");
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * Whether the access includes `permission`: for every client, or also for
 * assigned clients only when `assigned` is true. The server decides; this
 * only hides what would be refused.
 */
export function can(access: StaffAccess | undefined, permission: StaffPermission, { assigned = false } = {}): boolean {
  return access?.grants.some((g) => g.permission === permission && (assigned || g.scope === "all")) ?? false;
}

export type PermissionGroup = "Clients" | "Team" | "Business" | "Production" | "Websites" | "Platform";

/** What each staff permission allows, in the words the role editor shows. */
export const PERMISSIONS: Record<StaffPermission, { label: string; description: string; group: PermissionGroup }> = {
  "platform.orgs.read": { group: "Clients", label: "See clients", description: "Open clients and their practices, read-only." },
  "platform.clients.manage": {
    group: "Clients",
    label: "Edit client content",
    description: "Profile, portfolio, chambers, FAQ drafts, brand kit, booking settings and website. Never approvals or billing.",
  },
  "platform.articles.manage": { group: "Clients", label: "Write articles", description: "Draft and edit articles for doctors; only the doctor publishes." },
  "platform.bookings.manage": { group: "Clients", label: "Live bookings", description: "See and confirm or decline appointments." },
  "platform.verification.manage": { group: "Clients", label: "Registration checks", description: "Verify or reject BMDC registration numbers." },
  "platform.users.manage": { group: "Team", label: "Manage people", description: "See users, onboard clients, disable staff and assign them to clients." },
  "platform.roles.manage": { group: "Team", label: "Manage roles", description: "Create roles and give staff their roles. Only within your own access." },
  "platform.catalog.manage": { group: "Business", label: "Packages and subscriptions", description: "Package components, plans, quotes and subscriptions." },
  "platform.payments.manage": { group: "Business", label: "Payments", description: "Invoices, payments, prepayments and refunds; sees prices." },
  "platform.projects.manage": { group: "Business", label: "Projects", description: "Managed-service projects." },
  "platform.deliverables.manage": { group: "Production", label: "Production", description: "Pipeline, shoot schedule and publishing log." },
  "platform.content.publish": { group: "Production", label: "Publish content", description: "Log doctor-approved content as published." },
  "platform.websites.manage": { group: "Websites", label: "Doctor websites", description: "Domains, online/offline, templates and custom designs." },
  "platform.cms.manage": { group: "Platform", label: "Site content", description: "Edit and publish the BrandEx public site." },
  "platform.audit.read": { group: "Platform", label: "Audit log", description: "Read the record of sensitive actions." },
  "platform.integrations.manage": {
    group: "Platform",
    label: "Social integrations",
    description: "Enter BrandEx's app keys for YouTube, Meta, TikTok, LinkedIn and Google. Secrets are write-only.",
  },
};

export const GROUPS: PermissionGroup[] = ["Clients", "Team", "Business", "Production", "Websites", "Platform"];
